import React, { useCallback, useRef, useState } from 'react'

export function useFileDrop(onFiles: (files: File[]) => void) {
  const isDraggingRef = useRef(false)
  const [isDraggingFiles, setIsDraggingFiles] = useState(false)

  const isFileDrag = useCallback(
    (event: React.DragEvent) => Array.from(event.dataTransfer.types).includes('Files'),
    [],
  )

  const setDraggingVisible = useCallback((value: boolean) => {
    if (isDraggingRef.current === value) return
    isDraggingRef.current = value
    setIsDraggingFiles(value)
  }, [])

  const handleDragEnter = useCallback(
    (event: React.DragEvent) => {
      if (!isFileDrag(event)) return
      event.preventDefault()
      event.stopPropagation()
      setDraggingVisible(true)
    },
    [isFileDrag, setDraggingVisible],
  )

  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      if (!isFileDrag(event)) return
      event.preventDefault()
      event.stopPropagation()
      event.dataTransfer.dropEffect = 'copy'
    },
    [isFileDrag],
  )

  const handleDragLeave = useCallback(
    (event: React.DragEvent) => {
      if (!isFileDrag(event)) return
      event.preventDefault()
      event.stopPropagation()
      setDraggingVisible(false)
    },
    [isFileDrag, setDraggingVisible],
  )

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      if (!isFileDrag(event)) return
      event.preventDefault()
      event.stopPropagation()
      setDraggingVisible(false)
      const files = Array.from(event.dataTransfer.files)
      if (files.length) onFiles(files)
    },
    [onFiles, isFileDrag, setDraggingVisible],
  )

  return { isDraggingFiles, handleDragEnter, handleDragOver, handleDragLeave, handleDrop }
}
